/**
 * Communications hub service (F50) — the citizen inbox and the company's
 * per-talent Communications tab.
 */
import type {
  InboxItem,
  InboxItemState,
  InboxPage,
  InboxUnreadCount,
  InboxView,
  TalentCommunicationsPage,
} from '@tonnie/types';

import { TonnieClient } from '../client/base';

export class CommunicationsService {
  constructor(private client: TonnieClient) {}

  /** One page of the caller's inbox, newest first. Pure read. */
  async getInbox(params: { view: InboxView; cursor?: string; limit?: number }): Promise<InboxPage> {
    const query = new URLSearchParams({ view: params.view });
    if (params.cursor) query.set('cursor', params.cursor);
    if (params.limit) query.set('limit', String(params.limit));
    return this.client.get<InboxPage>(`/api/inbox?${query.toString()}`);
  }

  /** The person's one unread number — cheap enough to poll. Pure read. */
  async getInboxUnreadCount(): Promise<InboxUnreadCount> {
    return this.client.get<InboxUnreadCount>('/api/inbox/unread-count');
  }

  /** One of the caller's messages, in whichever view it sits. Pure read. */
  async getInboxItem(id: string): Promise<InboxItem> {
    return this.client.get<InboxItem>(`/api/inbox/${id}`);
  }

  /** Mark every unread message read; answers the count after (0). */
  async markAllInboxRead(): Promise<InboxUnreadCount> {
    return this.client.post<InboxUnreadCount>('/api/inbox/read-all', {});
  }

  async markInboxItemRead(id: string): Promise<InboxItemState> {
    return this.client.patch<InboxItemState>(`/api/inbox/${id}/read`, {});
  }

  /** Clears the read wherever it shows — the company's tab included. */
  async markInboxItemUnread(id: string): Promise<InboxItemState> {
    return this.client.patch<InboxItemState>(`/api/inbox/${id}/unread`, {});
  }

  async archiveInboxItem(id: string): Promise<InboxItemState> {
    return this.client.patch<InboxItemState>(`/api/inbox/${id}/archive`, {});
  }

  async restoreInboxItem(id: string): Promise<InboxItemState> {
    return this.client.patch<InboxItemState>(`/api/inbox/${id}/restore`, {});
  }

  /** What the company sent one talent, and what happened to it. No copy, no address. */
  async getTalentCommunications(
    talentId: string,
    params: { cursor?: string; limit?: number } = {}
  ): Promise<TalentCommunicationsPage> {
    const query = new URLSearchParams();
    if (params.cursor) query.set('cursor', params.cursor);
    if (params.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return this.client.get<TalentCommunicationsPage>(
      `/api/business/talents/${talentId}/communications${qs ? `?${qs}` : ''}`
    );
  }
}

export function createCommunicationsService(client: TonnieClient): CommunicationsService {
  return new CommunicationsService(client);
}
